import { Box } from '@mui/material';
import { GetServerSidePropsContext } from 'next';
import React from 'react';
import { jsonLdScriptProps } from 'react-schemaorg';
import { JobPosting } from 'schema-dts';

import GoogleAd from '@/components/ads/google-ads';
import JobDetailsCard from '@/components/jobs/job-details-card';
import JobList from '@/components/jobs/job-list';
import SearchJob from '@/components/pageLayout/search-job';
import SiteBreadCrumbs from '@/components/pageLayout/site-breadcrumbs';
import SiteFooter from '@/components/pageLayout/site-footer/site-footer';
import Old_SiteHeader from '@/components/pageLayout/site-header/old_site-header';
import SEOHead from '@/components/seo-head';
import globalMeta from '@/constants/global-meta';
import JobService from '@/services/Job.service';
import { Job } from '@/types/job';
import { monetaryMask } from '@/utils/monetary-mask';
import GtagService from '@/services/Gtag.service';
import PageNotFound from '@/pages/404';
import { formatJobTitle } from '@/utils/format-job-title';

interface JobDetailsPageProps {
  job: Job;
  similarJobs: Job[];
  jsonLdDescription: string;
  notFound?: boolean;
}

export default class JobDetailsPage extends React.Component<JobDetailsPageProps> {
  componentDidMount(): void {
    if (this.props.notFound) return;

    const { job } = this.props;
    GtagService.jobView(job);

    const searchJobHeight = window.innerWidth < 768 ? 244 : 112;
    const spacingTop = 24;
    window.scrollTo({ behavior: `smooth`, top: searchJobHeight + spacingTop });
  }

  render(): React.ReactNode {
    if (this.props.notFound) return <PageNotFound />;

    const { job, similarJobs, jsonLdDescription } = this.props;

    const jobTitle = formatJobTitle(job.title);
    const location = job.city ? ` em ${job.city}/${job.state} ` : ``;

    const title = `Vaga de ${jobTitle}${location}- ${job.id} - O Amarelinho`;
    const description = `Vaga de emprego de ${jobTitle}${location}- ${job.companyName} - Código da Vaga: ${job.id}. Candidate-se gratuitamente no portal do O Amarelinho!`;
    const canonicalUrl = `${globalMeta.siteUrl}/vagas/emprego/${job.slug}`;

    const jobApplicationDisabled = job.status === `DISABLED` || job.expired;

    return (
      <>
        <SEOHead title={title} description={description} canonicalUrl={canonicalUrl}>
          <meta name="robots" content="index,follow" />
        </SEOHead>

        {!jobApplicationDisabled && (
          <script
            {...jsonLdScriptProps<JobPosting>({
              '@context': `https://schema.org`,
              '@type': `JobPosting`,
              hiringOrganization: {
                '@type': `Organization`,
                name: job.companyName.toLowerCase().includes(`empresa confidencial`)
                  ? `confidential`
                  : job.companyName,
              },
              baseSalary: {
                '@type': `MonetaryAmount`,
                currency: `BRL`,
                value: { '@type': `QuantitativeValue`, unitText: `MONTH`, value: job.salary ? job.salary + `` : `` },
              },
              datePosted: job.publishStartDate?.slice(0, 10), // dd-MM-yyyy
              validThrough: job.validThrough?.slice(0, 10), // dd-MM-yyyy
              description: jsonLdDescription,
              qualifications: job.experienceAndQualification || ``,
              jobLocation: {
                '@type': `Place`,
                address: [
                  {
                    '@type': `PostalAddress`,
                    addressCountry: `BR`,
                    addressLocality: job.city,
                    addressRegion: job.state,
                    postalCode: ``,
                    streetAddress: ``,
                  },
                ],
              },
              salaryCurrency: `BRL`,
              title: jobTitle,
              workHours: ``,
              experienceRequirements: job.experienceAndQualification || ``,
            })}
          />
        )}

        <Old_SiteHeader />
        <main className="page-content container job-details">
          <SearchJob />
          <SiteBreadCrumbs
            othersLinksBeforeName={[
              { name: `Vagas de ${formatJobTitle(job.roleTitle)}`, link: `/vagas/cargo/${job.roleCode}-${job.roleSlug}` },
            ]}
            name={jobTitle}
          />
          <Box className="job-details_job-list">
            <JobDetailsCard job={job} className="item padding" linkToSearch={true} titleWithH1 />
            <aside className="item">
              {similarJobs && similarJobs.length > 0 && (
                <>
                  <h2>Vagas que podem te interessar</h2>
                  <JobList jobs={similarJobs} />
                </>
              )}
              <GoogleAd slot="5179896375" googleAdId="ca-pub-9646893046773308" />
            </aside>
          </Box>
        </main>

        <SiteFooter />
      </>
    );
  }
}

const generateJsonLdDescription = (job: Job) => {
  return `${job.benefits ? `Benefícios: ${job.benefits};` : ``}
${job.companyRole ? `Ramo de atividade: ${job.companyRole};` : ``}
${job.salary ? `Salário: R$${monetaryMask(job.salary)};` : ``}
${job.jobAmount ? `Quantidade de vagas: ${job.jobAmount} vaga${job.jobAmount > 1 ? `s` : ``};` : ``}
${job.activities ? `Informações Gerais: ${job.activities};` : ``}
${
  job.hiringContractTypes && job.hiringContractTypes.length > 0
    ? `Regime de contratação: ${job.hiringContractTypes.join(`, `)};`
    : ``
}
${job.weeklyWorkingHours ? `Jornada de trabalho: ${job.weeklyWorkingHours} horas semanais;` : ``}
${job.extraInformation ? `Mais informações sobre a vaga de emprego: ${job.extraInformation};` : ``}
${job.experiences && job.experiences.length > 0 ? `Requisitos: ${job.experiences.join(`, `)};` : ``}
${job.academicLevel ? `Escolaridade mínima: ${job.academicLevel};` : ``}
${job.languages ? job.languages.map(({ level, name }) => `Idioma: ${name} - Fluência: ${level}`).join(`; `) : ``}
`
    .replace(/\n{2,}/g, `\n`)
    .trim();
};

export async function getServerSideProps(context: GetServerSidePropsContext<{ id: string }>) {
  try {
    if (context.params && context.params.id) {
      const originalSlug = context.params.id;
      const jobId = parseInt(context.params.id as string); // transform 1234-asdf in 1234
      const queryParams = new URLSearchParams(context.query as Record<string, string>).toString();
      // ?nocache=1 fura o cache do CloudFront da API (cb único na query) — para
      // o backoffice conferir uma edição recém-aplicada. /n/{id} repassa a query,
      // então https://oamarelinho.com.br/n/123?nocache=1 também funciona.
      const fresh = context.query.nocache === `1` || context.query.nocache === `true`;

      JobService.incrementDetailsView(jobId);

      const [job, similarJobs] = await Promise.all([
        JobService.fetchJobById(jobId, fresh),
        JobService.fetchSimilarJobs(jobId),
      ]);

      if (job.slug && originalSlug !== job.slug) {
        return {
          redirect: {
            permanent: true,
            destination: `/vagas/emprego/${job.slug}${queryParams ? `?${queryParams}` : ``}`,
          },
        };
      }

      return {
        props: { job, similarJobs, jsonLdDescription: generateJsonLdDescription(job) },
      };
    } else {
      throw new Error(`Rota sem o id da vaga`);
    }
  } catch (e) {
    // Nao usar `notFound: true`: o @opennextjs/cloudflare nao renderiza a
    // pagina 404 nesse caminho e devolve 28 bytes de texto cru
    // ("This page could not be found"). Medido no mesmo build: runtime Node
    // 5.976 bytes com a 404 do projeto, workerd 28 bytes. Setando o status na
    // resposta e renderizando a 404 aqui, os dois runtimes entregam a mesma
    // pagina. Voltar para `notFound: true` quando o adapter corrigir.
    context.res.statusCode = 404;
    return {
      props: { notFound: true },
    };
  }
}
